import axios from "axios";
import { toast } from "sonner";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Demo-mode flag - set by AuthProvider once auth state is resolved.
// When true, GET requests are rewritten to /api/demo/*, write requests are
// rejected with a sentinel error so the UI can show LoginRequiredModal.
let _isDemo = false;
export const setDemoMode = (v) => { _isDemo = !!v; };
export const isDemoMode = () => _isDemo;

// Listener for write-attempt-while-in-demo. Set by App so the global modal
// can be triggered without a circular import.
let _onLoginRequired = null;
export const onLoginRequired = (fn) => { _onLoginRequired = fn; };

export const DEMO_LOGIN_REQUIRED = "DEMO_LOGIN_REQUIRED";

export const api = axios.create({
  baseURL: API,
});

// Request interceptor: attach Supabase access token + demo-mode handling
api.interceptors.request.use(async (config) => {
  // Always pull the latest token from Supabase (the SDK auto-refreshes it).
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {}

  // In demo mode, intercept based on HTTP method
  if (_isDemo) {
    const method = (config.method || "get").toLowerCase();
    const url = config.url || "";

    // Allow auth + public + admin + billing endpoints to pass through unchanged
    const isAuthRoute = url.startsWith("/auth/") || url.startsWith("/public/") || url.startsWith("/admin/") || url.startsWith("/billing/");
    const isAlreadyDemo = url.startsWith("/demo/");

    if (!isAuthRoute && !isAlreadyDemo) {
      if (method === "get") {
        // Rewrite GET /X → GET /demo/X (best-effort; unmatched fall through and 404)
        config.url = `/demo${url.startsWith("/") ? url : `/${url}`}`;
      } else {
        // Block writes: surface to the LoginRequiredModal and dismiss any
        // duplicate component-level toasts that fire from the rejected
        // promise's .catch handlers - modal alone is the right UX.
        if (_onLoginRequired) _onLoginRequired();
        setTimeout(() => toast.dismiss(), 250);
        const err = new Error("Login to perform this action");
        err.code = DEMO_LOGIN_REQUIRED;
        err.response = { data: { detail: "Login to perform this action" }, status: 401 };
        return Promise.reject(err);
      }
    }
  }

  return config;
});

// On 401, try one transparent refresh - Supabase JS auto-refreshes but if the
// refresh token is expired the user truly needs to sign in again.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    if (status === 401 && !config.__retried) {
      try {
        const { data } = await supabase.auth.refreshSession();
        const newToken = data?.session?.access_token;
        if (newToken) {
          config.__retried = true;
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newToken}`;
          return api(config);
        }
      } catch {}
    }
    return Promise.reject(error);
  }
);

export const endpoints = {
  me: () => api.get("/auth/me"),
  demoMe: () => api.get("/demo/me"),
  signupComplete: (body) => api.post("/auth/signup-complete", body),
  oauthComplete: (body) => api.post("/auth/oauth-complete", body || {}),
  logout: () => api.post("/auth/logout"),

  // Tasks
  listTasks: () => api.get("/tasks"),
  createTask: (body) => api.post("/tasks", body),
  updateTask: (id, body) => api.patch(`/tasks/${id}`, body),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  listGymStaff: () => api.get("/tasks/staff"),
  syncTodayTasks: () => api.post("/tasks/sync-today"),
  aiCreateTask: (description) => api.post("/tasks/ai-create", { description }),

  dashboard: () => api.get("/dashboard"),

  listLeads: () => api.get("/leads"),
  createLead: (body) => api.post("/leads", body),
  getLead: (id) => api.get(`/leads/${id}`),
  updateLead: (id, body) => api.patch(`/leads/${id}`, body),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  logLeadMessage: (id, body) => api.post(`/leads/${id}/messages`, body),
  convertLead: (id, body) => api.post(`/leads/${id}/convert`, body),
  scheduleTrial: (id, body) => api.post(`/leads/${id}/schedule-trial`, body),
  completeTrial: (id, body) => api.post(`/leads/${id}/complete-trial`, body),

  listMembers: () => api.get("/members"),
  createMember: (body) => api.post("/members", body),
  getMember: (id) => api.get(`/members/${id}`),
  renewMember: (id, body) => api.post(`/members/${id}/renew`, body),
  markVisit: (id) => api.post(`/members/${id}/visit`),
  logMemberMessage: (id, body) => api.post(`/members/${id}/messages`, body),
  deleteMember: (id) => api.delete(`/members/${id}`),

  getSettings: () => api.get("/settings"),
  updateSettings: (body) => api.put("/settings", body),

  addPayment: (id, body) => api.post(`/members/${id}/payments`, body),
  deletePayment: (id, pid) => api.delete(`/members/${id}/payments/${pid}`),
  setPaymentPlan: (id, body) => api.put(`/members/${id}/payment-plan`, body),
  setOnboardingStage: (id, body) => api.post(`/members/${id}/onboarding-stage`, body),
  completeOnboarding: (id, body) => api.post(`/members/${id}/complete-onboarding`, body),
  setWaiver: (id, body) => api.put(`/members/${id}/waiver`, body),
  importMembers: (body) => api.post("/members-bulk/import", body),
  importLeads: (body) => api.post("/leads-bulk/import", body),

  regeneratePin: (id) => api.post(`/members/${id}/regenerate-pin`),
  attendanceCheckin: (body) => api.post("/attendance/checkin", body),
  attendanceToday: () => api.get("/attendance/today"),
  attendanceSeries: (days = 7) => api.get(`/attendance/series?days=${days}`),
  memberStats: (id) => api.get(`/members/${id}/stats`),

  // AI
  aiSuggest: (body) => api.post("/ai/suggest", body),
  aiInsights: () => api.get("/ai/insights"),
  aiPlan: (body) => api.post("/ai/plan", body),
  getMemberPlan: (id) => api.get(`/members/${id}/plan`),
  clearMemberPlan: (id) => api.delete(`/members/${id}/plan`),

  // Calendar
  listTrainers: () => api.get("/trainers"),
  createTrainer: (body) => api.post("/trainers", body),
  updateTrainer: (id, body) => api.patch(`/trainers/${id}`, body),
  deleteTrainer: (id) => api.delete(`/trainers/${id}`),

  listClasses: () => api.get("/classes"),
  createClass: (body) => api.post("/classes", body),
  updateClass: (id, body) => api.patch(`/classes/${id}`, body),
  deleteClass: (id) => api.delete(`/classes/${id}`),

  listClassBookings: (classId, date) => api.get(`/classes/${classId}/bookings?date=${date}`),
  createClassBooking: (classId, body) => api.post(`/classes/${classId}/bookings`, body),
  updateBooking: (id, status) => api.patch(`/bookings/${id}?status=${status}`),
  deleteBooking: (id) => api.delete(`/bookings/${id}`),

  listEvents: (upcomingOnly = false) => api.get(`/events?upcoming_only=${upcomingOnly}`),
  createEvent: (body) => api.post("/events", body),
  updateEvent: (id, body) => api.patch(`/events/${id}`, body),
  deleteEvent: (id) => api.delete(`/events/${id}`),

  calendar: (startDate, view = "week") => api.get(`/calendar?start_date=${startDate}&view=${view}`),

  listReminders: (status = "pending") => api.get(`/reminders?status=${status}`),
  markReminderSent: (id) => api.post(`/reminders/${id}/sent`),
  skipReminder: (id) => api.post(`/reminders/${id}/skip`),
  runCron: () => api.post("/cron/run"),

  getGym: () => api.get("/gym"),
  updateGym: (body) => api.patch("/gym", body),
  rotateWebhook: () => api.post("/gym/webhook/rotate"),
  getTeam: () => api.get("/gym/team"),
  invite: (body) => api.post("/gym/invite", body),
  revokeInvite: (id) => api.delete(`/gym/invite/${id}`),
  removeTeamMember: (user_id) => api.delete(`/gym/team/${user_id}`),

  // Billing (Razorpay)
  billingPlans: () => api.get("/billing/plans"),
  billingMe: () => api.get("/billing/me"),
  billingCreateSubscription: (plan_key) => api.post("/billing/create-subscription", { plan_key }),
  billingCreateOrder: (plan_key) => api.post("/billing/create-order", { plan_key }),
  billingVerifyPayment: (body) => api.post("/billing/verify-payment", body),

  seed: () => api.post("/seed"),

  // Super admin (Fitnudge internal)
  adminOverview: () => api.get("/admin/overview"),
  adminGyms: (q = "") => api.get(`/admin/gyms${q ? `?q=${encodeURIComponent(q)}` : ""}`),
};
